'use client';
/**
 * lib/auth/client.ts — l'authentification vue du navigateur.
 *
 * PAS DE `baseURL` : les appels partent en relatif sur `/api/auth`. Une URL
 * absolue serait figée dans le bundle à la compilation, et changer de domaine
 * (ogryn-mvp → app.ogryn.eu) obligerait à tout recompiler pour que la connexion
 * remarche.
 */
import { useMemo } from 'react';
import { createAuthClient } from 'better-auth/react';
import { passkeyClient } from '@better-auth/passkey/client';
import { inferAdditionalFields, phoneNumberClient } from 'better-auth/client/plugins';
import type { Auth } from '@/lib/auth/better-auth';

export const authClient = createAuthClient({
  plugins: [
    passkeyClient(),
    // `signIn.phoneNumber`, `phoneNumber.sendOtp` et `phoneNumber.verify` :
    // les routes existent côté serveur, mais le client ne les expose que si le
    // greffon est déclaré ici aussi.
    phoneNumberClient(),
    // Sans lui, `ogrynRole` n'existerait pas côté client : les champs ajoutés au
    // schéma serveur ne sont pas devinés par le client.
    inferAdditionalFields<Auth>(),
  ],
});

export const { signIn, signUp, useSession } = authClient;

/** Le compte connecté, sous la forme qu'attendent les écrans. */
export type CompteUI = {
  id: string;
  name: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  imageUrl: string | null;
  /** Le rôle CHOISI à l'inscription. Ne donne aucun droit — il oriente. */
  role: 'prestataire' | 'client' | null;
};

/**
 * Remplace `useUser()` de Clerk, même forme de retour pour ne pas réécrire
 * chaque écran : `{ user, isLoaded, isSignedIn }`.
 *
 * `isLoaded` compte autant que `user` : pendant le premier rendu, on ne sait pas
 * encore si quelqu'un est connecté. Un écran qui traite « pas encore chargé »
 * comme « pas connecté » fait clignoter l'invite d'inscription au visiteur qui
 * l'est déjà (flash constaté le 30/07).
 */
export function useCompte(): { user: CompteUI | null; isLoaded: boolean; isSignedIn: boolean } {
  const { data, isPending } = useSession();
  const u = data?.user;
  const role = (u as { ogrynRole?: string | null } | undefined)?.ogrynRole;

  // LA MÊME IDENTITÉ TANT QUE LE COMPTE NE CHANGE PAS. Un objet neuf à chaque
  // rendu faisait tourner sans fin tout effet qui en dépend : SessionAlert
  // relançait /api/security et /api/devices après chaque réponse, ~1 900
  // appels par minute (tests/unit/use-compte-stable.test.mjs).
  const user = useMemo<CompteUI | null>(() => {
    if (!u) return null;
    const parts = (u.name ?? '').trim().split(/\s+/).filter(Boolean);
    return {
      id: u.id,
      name: u.name ?? '',
      firstName: parts[0] ?? null,
      lastName: parts.slice(1).join(' ') || null,
      email: u.email ?? null,
      imageUrl: u.image ?? null,
      role: role === 'prestataire' ? 'prestataire' : role === 'client' ? 'client' : null,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `u` est recréé par la session à chaque rendu : ce sont ses champs qui comptent
  }, [u?.id, u?.name, u?.email, u?.image, role]);

  return { user, isLoaded: !isPending, isSignedIn: !!user };
}

/** Ferme la session et renvoie où il faut. Remplace `useClerk().signOut()`. */
export async function seDeconnecter(redirectUrl = '/bienvenue') {
  await authClient.signOut();
  window.location.href = redirectUrl;
}

/**
 * Envoie sur l'écran d'inscription. Remplace `useClerk().openSignUp()`.
 *
 * Clerk ouvrait une fenêtre par-dessus la page ; ici l'inscription est un écran
 * à part entière — `/bienvenue`. On y emporte l'adresse de retour pour revenir
 * exactement où l'utilisateur avait cliqué.
 */
export function ouvrirInscription(retour?: string) {
  const u = new URL('/bienvenue', window.location.origin);
  u.searchParams.set('inscription', '1');
  if (retour) u.searchParams.set('redirect_url', retour);
  window.location.href = u.toString();
}

/** Envoie sur l'écran de connexion, même principe. */
export function ouvrirConnexion(retour?: string) {
  const u = new URL('/bienvenue', window.location.origin);
  if (retour) u.searchParams.set('redirect_url', retour);
  window.location.href = u.toString();
}
